<script setup lang="ts">
import type { SearchClaimDocument } from "@upclaim/sdk/src/data-contracts";
import {
  Combobox,
  ComboboxLabel,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/24/solid'
import { ref, watch } from 'vue';

defineProps<{
  attribute: string;
  attributeName: string;
  size?: "md" | "lg" | "xl";
}>()

interface AisRefinements {
  items: any;
  isShowingMore: any;
  isFromSearch: any;
  canToggleShowMore: any;
  refine: any;
  createURL: any;
  toggleShowMore: any;
  searchForItems: any;
  sendEvent: any;
}

const query = ref("");

function search(value: string, refinements: any) {
  query.value = value;
  refinements.searchForItems(query.value);
}

const selected = ref([]);
</script>
<template>
  <ais-refinement-list  :attribute="attribute" operator="or" searchable :limit="200">
    <template v-slot="refinements">
      <div class="pt-4 pb-10" v-if="refinements.items.length > 1 || query">
        <Combobox v-model="selected" v-slot="{ open, disabled, activeIndex, activeOption }" @update:model-value="query = ''" multiple>
        <ComboboxLabel class="block text-sm font-medium text-gray-700">{{ attributeName }}</ComboboxLabel>
        <div class="relative mt-1">
          <ComboboxButton class="w-full">
            <ComboboxInput type="search"
              @keyup.enter="refinements.refine(activeOption || '')"
              class="py-2 pl-3 pr-10 bg-white border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
              :class="[size === 'lg' && 'w-72', size === 'md' && 'w-56', size === 'xl' && 'w-96']"
              :display-value="() => query" :placeholder="refinements.items.some(item => item.isRefined)
                ? refinements.items.filter(item => item.isRefined).map(item => item.value)
                : `${attributeName}...`" @change="search(($event.currentTarget as any).value, refinements)" />
            <kbd
              class="absolute inset-y-0 right-0 flex items-center px-2 text-gray-400 rounded-r-md focus:outline-none">
              <ChevronUpDownIcon class="w-5 h-5" aria-hidden="true" />
            </kbd>
          </ComboboxButton>

          <TransitionRoot leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <ComboboxOptions
              class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              :key="refinements.length">
              <div v-if="refinements.isFromSearch && !refinements.items.length"
                class="relative px-4 py-2 text-gray-700 cursor-default select-none">
                Pas de résultat.
              </div>

              <ComboboxOption v-for="item in refinements.items"
                as="template"
                :key="item.value"
                :value="item.value"
                v-slot="{ selected, active }"
              >
                <li v-if="item.value" class="relative py-2 pl-10 pr-4 cursor-default select-none" :class="{
                  'bg-blue-600 text-white': active,
                  'text-gray-900': !active,
                }">
                  <a
                    :href="refinements.createURL(item)"
                    @click.prevent="refinements.refine(item.value)"
                    class="block truncate" :style="{ fontWeight: item.isRefined ? 'bold' : 'normal' }"
                    :class="{ 'font-medium': selected, 'font-normal': !selected }">
                    <ais-highlight attribute="item" :hit="item" />
                    <span class="text-sm" :class="active ? 'text-blue-100' : 'text-gray-400'">
                      ({{ item.count }})
                    </span>
                  </a>

                  <span v-if="item.isRefined || selected" class="absolute inset-y-0 left-0 flex items-center pl-3"
                    :class="{ 'text-white': selected, 'text-blue-600': !selected }">
                    <CheckIcon class="w-5 h-5" aria-hidden="true" />
                  </span>
                </li>
              </ComboboxOption>
            </ComboboxOptions>
          </TransitionRoot>
        </div>
      </Combobox>
      </div>
      <div v-else></div>
    </template>
  </ais-refinement-list>
</template>
<style>
.ais-Highlight-highlighted {
  @apply bg-transparent !important;
  @apply font-bold !important;
  @apply text-inherit !important;
}
</style>